import React, { useState, useRef, useEffect } from 'react';
import { CalculatorForm } from './components/CalculatorForm';
import { ResultsDisplay } from './components/ResultsDisplay';
import { RealizedBenefitOverTimeChart } from './components/charts/RealizedBenefitOverTimeChart';
import { ErrorCalculatorForm } from './components/ErrorCalculatorForm';
import { ErrorResultsDisplay } from './components/ErrorResultsDisplay';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ErrorBoundary } from './components/ErrorBoundary';
import { EPTTrendChart } from './components/charts/EPTTrendChart';
import { ProductivityTrendChart } from './components/charts/ProductivityTrendChart';
import { AnnualHoursImpactTrendChart } from './components/charts/AnnualHoursImpactTrendChart';
import { EPTDifferenceTrendChart } from './components/charts/EPTDifferenceTrendChart';
import { DailyProductivityTrendChart } from './components/charts/DailyProductivityTrendChart';
import { TargetProductivityVsEPTChart } from './components/charts/TargetProductivityVsEPTChart';
import { ErrorAnalyticsDashboard } from './components/charts/ErrorAnalyticsDashboard';
import { FileText, Download, FileDown, Zap as ZapIcon, AlertTriangle, TrendingUp } from 'lucide-react';
import { Tab } from '@headlessui/react';
import { exportCharts } from './utils/exportCharts';
import { CalculationResult } from './types';
import { ErrorCalculationResult } from './types/errorCohorts';
import { CalculatorManager } from './components/calculator/CalculatorManager';
import { useCalculatorState } from './hooks/useCalculatorState';

export default function App() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [errorFormCount, setErrorFormCount] = useState(1);
  const [errorResults, setErrorResults] = useState<ErrorCalculationResult[]>([]);
  const [showErrorTrend, setShowErrorTrend] = useState(false);
  const [lastCalculatedErrorValues, setLastCalculatedErrorValues] = useState<Partial<ErrorCalculationResult> | null>(null);
  const [lastCalculatedErrorIndex, setLastCalculatedErrorIndex] = useState<number | null>(null);
  const { state: calculatorState, updateState: setCalculatorState } = useCalculatorState();

  const citationsRef = useRef<HTMLDetailsElement>(null);
  const calculatorRefs = useRef<(HTMLDivElement | null)[]>([]);
  const resultsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const errorCalculatorRefs = useRef<(HTMLDivElement | null)[]>([]);
  const errorResultsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const trendsRef = useRef<HTMLDivElement>(null);
  const errorTrendsRef = useRef<HTMLDivElement>(null);

  // Preserve calculator state when switching tabs
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem('calculatorState', JSON.stringify(calculatorState));
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [calculatorState]);

  const handleShowTrend = () => {
    setCalculatorState(prev => ({ ...prev, showTrend: true }));
    setTimeout(() => {
      if (trendsRef.current) {
        trendsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleShowErrorTrend = () => {
    setShowErrorTrend(true);
    setTimeout(() => {
      if (errorTrendsRef.current) {
        errorTrendsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const tabPanels = [
    {
      title: "Speed",
      icon: ZapIcon,
      content: (
        <div className="space-y-6">
          <div className="flex gap-4 flex-wrap print:hidden">
            {calculatorState.results.length > 0 && (
              <>
                <button
                  onClick={() => exportCharts('pdf')}
                  className="btn-primary flex items-center gap-2"
                >
                  <FileDown size={20} />
                  Export Charts to PDF
                </button>
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.download = 'productivity-analysis.csv';
                    link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(
                      [
                        ['Role', 'Cost/Employee', 'Tasks/Day', 'Productivity %', 'Value Produced', 'True Cost'].join(','),
                        ...calculatorState.results.map(r => 
                          [r.role, r.cost, r.tasksPerDay, r.productivity.toFixed(1), r.valueProduced.toFixed(2), r.trueCost.toFixed(2)].join(',')
                        )
                      ].join('\n')
                    )}`;
                    link.click();
                  }}
                  className="btn-primary flex items-center gap-2"
                >
                  <Download size={20} />
                  Export to CSV
                </button>
              </>
            )}
          </div>

          <CalculatorManager
            onShowTrend={handleShowTrend}
            state={calculatorState}
            onChange={setCalculatorState}
            lastCalculatedResult={calculatorState.results[calculatorState.results.length - 1]}
          />

          {calculatorState.showTrend && calculatorState.results.length > 0 && (
            <div ref={trendsRef} className="space-y-8">
              {calculatorState.results.some(r => r.knownExperiencesMode) ? (
                <>
                  <ErrorBoundary>
                    <TargetProductivityVsEPTChart results={calculatorState.results} />
                  </ErrorBoundary>
                  <ErrorBoundary>
                    <AnnualHoursImpactTrendChart results={calculatorState.results} />
                  </ErrorBoundary>
                  <ErrorBoundary>
                    <RealizedBenefitOverTimeChart results={calculatorState.results} />
                  </ErrorBoundary>

                  <ErrorBoundary>
                    <DailyProductivityTrendChart results={calculatorState.results} />
                  </ErrorBoundary>
                </>
              ) : (
                <>
                  <ErrorBoundary>
                    <EPTTrendChart results={calculatorState.results} />
                  </ErrorBoundary>
                  <ErrorBoundary>
                    <ProductivityTrendChart results={calculatorState.results} />
                  </ErrorBoundary>
                  <ErrorBoundary>
                    <AnnualHoursImpactTrendChart results={calculatorState.results} />
                  </ErrorBoundary>
                </>
              )}
            </div>
          )}
        </div>
      )
    },
    {
      title: "Errors",
      icon: AlertTriangle,
      content: (
        <div className="space-y-6">
          <div className="flex gap-4 flex-wrap print:hidden">
            <button
              onClick={() => setErrorFormCount(prev => prev + 1)}
              className="btn-primary flex items-center gap-2"
            >
              <FileText size={20} />
              Add Error Calculator
            </button>

            {errorResults.length > 0 && (
              <>
                <button
                  onClick={() => exportCharts('pdf')}
                  className="btn-primary flex items-center gap-2"
                >
                  <FileDown size={20} />
                  Export Charts to PDF
                </button>
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.download = 'error-impact-analysis.csv';
                    link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(
                      [
                        ['Error Type', 'Annual Errors', 'Productivity Loss %', 'Hours Lost', 'Financial Impact'].join(','),
                        ...errorResults.map(r => 
                          [r.errorType, r.annualErrors, r.productivityLoss.toFixed(1), r.errorImpactHours.toFixed(1), r.trueLaborCost.toFixed(2)].join(',')
                        )
                      ].join('\n')
                    )}`;
                    link.click();
                  }}
                  className="btn-primary flex items-center gap-2"
                >
                  <Download size={20} />
                  Export to CSV
                </button>
              </>
            )}
          </div>

          {Array.from({ length: errorFormCount }).map((_, index) => (
            <div key={index} className="space-y-4">
              <div ref={el => errorCalculatorRefs.current[index] = el}>
                <ErrorBoundary>
                  <ErrorCalculatorForm 
                    onCalculate={(result) => {
                      const newResults = [...errorResults];
                      newResults[index] = result;
                      setErrorResults(newResults);
                      setLastCalculatedErrorIndex(index);
                      setLastCalculatedErrorValues(result);
                    }}
                    onDelete={index > 0 ? () => {
                      const newResults = [...errorResults];
                      newResults.splice(index, 1);
                      setErrorResults(newResults);
                      setErrorFormCount(prev => prev - 1);
                    } : undefined}
                    formIndex={index}
                    resultsRef={{ current: errorResultsRefs.current[index] }}
                    previousValues={lastCalculatedErrorValues}
                  />
                </ErrorBoundary>
              </div>
              <div ref={el => errorResultsRefs.current[index] = el}>
                {errorResults[index] && (
                  <ErrorBoundary>
                    <ErrorResultsDisplay 
                      result={errorResults[index]}
                      calculatorRef={{ current: errorCalculatorRefs.current[index] }}
                      onShowTrend={handleShowErrorTrend}
                    />
                  </ErrorBoundary>
                )}
              </div>
            </div>
          ))}

          {errorResults.length > 0 && (
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setErrorFormCount(prev => prev + 1)}
                className="btn-primary flex items-center gap-2"
              >
                <FileText size={20} />
                Add Error Calculator
              </button>
              <button
                onClick={handleShowErrorTrend}
                className="btn-primary flex items-center gap-2"
              >
                <TrendingUp size={20} />
                Show Trend
              </button>
            </div>
          )}

          {showErrorTrend && errorResults.length > 0 && (
            <div ref={errorTrendsRef}>
              <ErrorBoundary>
                <ErrorAnalyticsDashboard results={errorResults} />
              </ErrorBoundary>
            </div>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 print:bg-white">
      <Header />

      <main className="flex-grow py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
            <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1 mb-6">
              {tabPanels.map((tab, index) => (
                <Tab
                  key={tab.title}
                  className={({ selected }) =>
                    `w-full rounded-lg py-2.5 text-sm font-medium leading-5 
                    ${selected 
                      ? 'bg-white text-blue-700 shadow'
                      : 'text-blue-500 hover:bg-white/[0.12] hover:text-blue-600'
                    } 
                    flex items-center justify-center gap-2 transition-all duration-200`
                  }
                >
                  {React.createElement(tab.icon, { size: 16 })}
                  {tab.title}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              {tabPanels.map((tab, index) => (
                <Tab.Panel
                  key={index}
                  className={selectedTab === index ? 'block' : 'hidden'}
                >
                  {tab.content}
                </Tab.Panel>
              ))}
            </Tab.Panels>
          </Tab.Group>
        </div>
      </main>

      <Footer citationsRef={citationsRef} />
    </div>
  );
}